import React from 'react';

import {
  ConsumerUsageSummaryQuery,
  paymentDataFragment,
} from 'apollo/queries/autogenerate/operations';
import { InstallmentPlanKind, Tier } from 'apollo/queries/autogenerate/schemas';
import { AMZ_SUB_MERCHANTS_IDS } from 'constants/amazon';
import ListItem from 'shared/modules/usage/components/molecules/ListItem';
import { formatYearMonthDate } from 'shared/utils/japanese';
import testID from 'shared/utils/test-id';
import { convertAmazonPayTitle } from 'utils/amazon-pay-default-storename';

type Props = {
  payment: ConsumerUsageSummaryQuery['consumerUsageSummary']['uncapturedPayments'][0];
  onPress: (payment: paymentDataFragment) => void;
  id?: string;
};

export default function UncapturedPayment(props: Props) {
  const payment = props.payment;
  const isAmazonPay = payment.subMerchantId === AMZ_SUB_MERCHANTS_IDS.amazonPay;

  // If the storeName of Amazon Pay purchase starts with 'AMZ', should display default title
  const shouldConvertAmazonPayDefaultTitle =
    !!payment.storeName && convertAmazonPayTitle(payment.storeName);

  // If the charged payment is Amazon Pay purchase, should display following title
  const amazonPayTitle = isAmazonPay
    ? payment.storeName === null ||
      payment.storeName === '<NO NAME>' ||
      shouldConvertAmazonPayDefaultTitle
      ? 'Amazon Pay提携サイト'
      : payment.storeName
    : null;

  // If the payment is Amazon Purchase besides Amazon Pay and the storeName is null, should display the default value 'Amazon.co.jp'
  const isAmazon =
    payment.subMerchantId === AMZ_SUB_MERCHANTS_IDS.amazonPrime ||
    AMZ_SUB_MERCHANTS_IDS.amazonKindle ||
    AMZ_SUB_MERCHANTS_IDS.amazonMusic ||
    AMZ_SUB_MERCHANTS_IDS.amazonPharmacy ||
    AMZ_SUB_MERCHANTS_IDS.amazonPrimeVideo ||
    AMZ_SUB_MERCHANTS_IDS.amazonRetail ||
    AMZ_SUB_MERCHANTS_IDS.amazonMarketplace;

  const amazonTitle = isAmazon
    ? payment.storeName === null || payment.storeName === '<NO NAME>'
      ? 'Amazon.co.jp'
      : payment.storeName
    : null;

  const title = amazonPayTitle ?? amazonTitle ?? payment.storeName;
  const subtitle = payment.byWayOf;
  const amazonPayDivisionTitle = isAmazonPay ? 'Amazon Pay' : null;
  const uncapturedAmount = payment.amount - payment.capturedAmount;
  let description;
  let descriptionTestID = {};

  if (payment.tier === Tier.PLUS) {
    description = '超あと払い';
    descriptionTestID = testID('is_plus');
  } else if (payment.installmentPlanKind === InstallmentPlanKind.THREE_PAY) {
    description = '３回あと払い';
    descriptionTestID = testID('is_3pay');
  } else if (payment.installmentPlanKind === InstallmentPlanKind.SIX_PAY) {
    description = '６回あと払い';
    descriptionTestID = testID('is_6pay');
  } else if (payment.installmentPlanKind === InstallmentPlanKind.TWELVE_PAY) {
    description = '12回あと払い';
    descriptionTestID = testID('is_12pay');
  }

  return (
    <ListItem
      iconName="captured"
      title={title || ''}
      subtitle={subtitle}
      divisiontitle={amazonPayDivisionTitle}
      date={formatYearMonthDate(payment.createdAt, 'en')}
      amount={uncapturedAmount}
      description={description}
      onPress={() => props.onPress(payment)}
      {...testID('container')}
      descriptionTestID={descriptionTestID}
      id={props.id}
    />
  );
}
